import { useMemo } from 'react';

import { Documents } from '@e-origin/shared';

import { useCustomers, useCustomersNamesAndIds } from '@hooks/customer';
import { useFinalizeFieldRules } from '../policy';

interface GoodsLocationOption {
  unLocode?: string;
  warehouses?: { type?: string; identifier?: string }[];
}

interface Params {
  brokerId: string;
  goodsLocations?: GoodsLocationOption[];
}

export const useGlobalParameterCascades = ({ brokerId, goodsLocations }: Params) => {
  const { form } = useFinalizeFieldRules();
  const filters = brokerId ? { filters: { brokerId, valid: true } } : undefined;
  const customersNamesAndIds = useCustomersNamesAndIds(filters);
  const { data: { list: customers } = {} } = useCustomers(filters);

  const selectedUnLocode = form.values.consignment.locationOfGoods.unLocode;

  const warehouseData = useMemo(() => {
    if (!selectedUnLocode || !goodsLocations) {
      return [];
    }

    const selectedLocation = goodsLocations.find((loc) => loc.unLocode === selectedUnLocode);

    return (
      selectedLocation?.warehouses?.map((warehouse) => ({
        label: `${warehouse.type} - ${warehouse.identifier}`,
        value: warehouse.identifier || '',
      })) || []
    );
  }, [selectedUnLocode, goodsLocations]);

  const customerValue = customersNamesAndIds.find((customer) => customer.value === form.values.customer)?.value;

  const updateCustomerHandler = (selectedOption: string) => {
    form.setFieldValue('customer', selectedOption);

    const newCustomer = customers.find((customer) => customer._id === selectedOption);

    form.setFieldValue('messageInfo.methodOfPayment', newCustomer.payments.methodOfPayment);
    form.setFieldValue('messageInfo.deferredPayment', newCustomer.payments.deferredPayment);
    form.setFieldValue('messageInfo.methodOfPaymentVAT', newCustomer.payments.methodOfPaymentVAT);

    form.setTouched({ customer: true });
  };

  const updateWarehouseHandler = (selectedWarehouseIdentifier: string) => {
    form.setFieldValue('warehouse', selectedWarehouseIdentifier);

    const selectedLocation = goodsLocations?.find((loc) => loc.unLocode === selectedUnLocode);
    const selectedWarehouse = selectedLocation?.warehouses?.find((w) => w.identifier === selectedWarehouseIdentifier);

    if (!selectedWarehouse) {
      return;
    }

    const warehouseDoc = { identifier: selectedWarehouse.identifier, type: selectedWarehouse.type, header: true };
    const currentDocs = (form.values.documents as Documents)?.warehouseDocuments || [];

    form.setFieldValue('documents.warehouseDocuments', currentDocs.length ? [warehouseDoc, ...currentDocs.slice(1)] : [warehouseDoc]);
  };

  return { customersNamesAndIds, customerValue, warehouseData, updateCustomerHandler, updateWarehouseHandler };
};
